// Dialogs and record panes that fade in and out (DESIGN.md 7.4) and still
// start fresh at every opening. Each one stays rendered between openings, so
// Mantine can play its fade both ways; `round` changes at each opening and the
// form inside is keyed by it, so nothing typed or chosen last time carries
// over, even when it is reopened while still closing.

import { useState } from 'react';

export interface Dialog {
  /** Whether to render the dialog at all: from its first opening on, so a table's rows render none until one is used. */
  mounted: boolean;
  opened: boolean;
  /** Which opening this is. */
  round: number;
  open: () => void;
  close: () => void;
}

/**
 * A dialog opened by a control. The first opening renders it closed and opens
 * it on the next frame, since a Modal that mounts already open skips its fade.
 */
export function useDialog(): Dialog {
  const [state, setState] = useState({ mounted: false, wanted: false, opened: false, round: 0 });
  const open = () => {
    setState((s) => ({ mounted: true, wanted: true, opened: s.mounted, round: s.round + 1 }));
    if (!state.mounted) requestAnimationFrame(() => setState((s) => ({ ...s, opened: s.wanted })));
  };
  const close = () => setState((s) => ({ ...s, wanted: false, opened: false }));
  return { mounted: state.mounted, opened: state.opened, round: state.round, open, close };
}

export interface Shown<T> {
  opened: boolean;
  /** The value it was opened with; after it closes, the last one, so it is not emptied while it fades out. */
  shown: T | null;
  /** Which opening this is. */
  round: number;
}

/** A dialog or pane opened with a value (a record, a key) and closed by clearing it. It is rendered always. */
export function useShown<T>(value: T | null | undefined): Shown<T> {
  const opened = value !== null && value !== undefined;
  const [held, setHeld] = useState<{ value: T | null; opened: boolean; round: number }>({ value: null, opened: false, round: 0 });
  const shown = opened ? value : held.value;
  const round = opened && !held.opened ? held.round + 1 : held.round;
  // Adjusting state while rendering, as React documents for a value derived
  // from a prop's history; it settles in the same render pass.
  if (opened !== held.opened || shown !== held.value) setHeld({ value: shown, opened, round });
  return { opened, shown, round };
}
